import * as React from "react";
import { Input } from "@/components/ui/input";
import { formatMoneyInput, parseMoneyBR } from "@/lib/money";

type MoneyInputProps = Omit<React.ComponentProps<typeof Input>, "type" | "inputMode" | "value" | "onChange"> & {
  value: string | number | null | undefined;
  onValueChange: (value: number) => void;
  allowNegative?: boolean;
};

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ value, onValueChange, allowNegative = false, onBlur, onFocus, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState(() => formatMoneyInput(value));
    const focusedRef = React.useRef(false);

    React.useEffect(() => {
      if (!focusedRef.current) setDisplayValue(formatMoneyInput(value));
    }, [value]);

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onFocus={(event) => {
          focusedRef.current = true;
          onFocus?.(event);
        }}
        onChange={(event) => {
          const sanitized = event.target.value.replace(allowNegative ? /[^\d.,-]/g : /[^\d.,]/g, "");
          setDisplayValue(sanitized);
          onValueChange(parseMoneyBR(sanitized));
        }}
        onBlur={(event) => {
          focusedRef.current = false;
          const parsed = parseMoneyBR(displayValue);
          setDisplayValue(formatMoneyInput(parsed));
          onValueChange(parsed);
          onBlur?.(event);
        }}
      />
    );
  }
);

MoneyInput.displayName = "MoneyInput";
